import { lazy } from "react";

import { AppRoutings } from "utility/enums/app-routings";
import { MenuName } from "utility/enums/user-access-right";

interface IRoute {
	path: string;
	Component: React.LazyExoticComponent<React.FC<{}>> | React.ComponentType<{}>;
	pageName: string; // required to display error with page name - if user do not have permission (e.g. "You do not have permission to access departments page.")
	moduleName?: string; // required to display error with module name - if user do not have permission (e.g. "You do not have permission to add/update/delete requisition detail.")
}

export const routes: IRoute[] = [
	{
		path: AppRoutings.ProcureToPayDashboard,
		Component: lazy(() => import("components/Dashboard/DashboardGrid")),
		pageName: MenuName.ProcureToPayDashboard,
	},
	{
		path: AppRoutings.InternalUsers,
		Component: lazy(() => import("components/UserManagement/InternalUser")),
		pageName: MenuName.InternalUsers,
	},
	{
		path: AppRoutings.InternalUserDetail,
		Component: lazy(
			() => import("components/UserManagement/InternalUser/InternalUserDetail")
		),
		pageName: MenuName.InternalUsers,
		moduleName: "Internal User",
	},
	{
		path: AppRoutings.RequisitionSummaryGrid,
		Component: lazy(
			() => import("components/Requisition/RequisitionSummaryGrid")
		),
		pageName: MenuName.Requisitions,
	},
	{
		path: AppRoutings.RequisitionEntry,
		Component: lazy(() => import("components/Requisition/RequisitionEntry")),
		pageName: MenuName.Requisitions,
		moduleName: "Requisition",
	},
	{
		path: AppRoutings.RequisitionEntryAction,
		Component: lazy(() => import("components/Requisition/RequisitionEntry")),
		pageName: MenuName.Requisitions,
		moduleName: "Requisition",
	},
	{
		path: AppRoutings.WorkflowSummaryGrid,
		Component: lazy(() => import("components/WorkFlow/WorkFlowSummaryGrid")),
		pageName: MenuName.Workflows,
		moduleName: "Workflows",
	},
	{
		path: AppRoutings.WorkflowDefinition,
		Component: lazy(() => import("components/WorkFlow/WorkflowDefinition")),
		pageName: MenuName.Workflows,
		moduleName: "Workflow Definition",
	},
	{
		path: AppRoutings.WorkflowStepDefinition,
		Component: lazy(() => import("components/WorkFlow/WorkflowStepDefinition")),
		pageName: MenuName.Workflows,
		moduleName: "Workflow Step Definition",
	},
	{
		path: AppRoutings.DepartmentSummaryGrid,
		Component: lazy(
			() =>
				import(
					"components/Settings/OrganisationalUnits/Department/DepartmentSummaryGrid"
				)
		),
		pageName: MenuName.Departments,
	},
	{
		path: AppRoutings.DepartmentDetail,
		Component: lazy(
			() =>
				import(
					"components/Settings/OrganisationalUnits/Department/DepartmentDetail"
				)
		),
		pageName: MenuName.Departments,
		moduleName: "Department",
	},
	{
		path: AppRoutings.ItemSummaryGrid,
		Component: lazy(
			() => import("components/MasterRecords/Item/ItemSummaryGrid")
		),
		pageName: MenuName.Items,
	},
	{
		path: AppRoutings.ItemDetail,
		Component: lazy(() => import("components/MasterRecords/Item/ItemDetail")),
		pageName: MenuName.Items,
		moduleName: "item",
	},
	{
		path: AppRoutings.ProgrammeSummaryGrid,
		Component: lazy(
			() => import("components/MasterRecords/Programme/ProgrammeSummaryGrid")
		),
		pageName: MenuName.Programmes,
	},
	{
		path: AppRoutings.ProgrammeDetail,
		Component: lazy(
			() => import("components/MasterRecords/Programme/ProgrammeDetail")
		),
		pageName: MenuName.Programmes,
		moduleName: "Programme",
	},
	{
		path: AppRoutings.RequisitionConversionToBid,
		Component: lazy(
			() => import("components/Requisition/RequisitionConversionToBid")
		),
		pageName: MenuName.Requisitions,
		moduleName: "Requisition To Bid",
	},
	{
		path: AppRoutings.RequisitionConversionToBidAction,
		Component: lazy(
			() => import("components/Requisition/RequisitionConversionToBid")
		),
		pageName: MenuName.Requisitions,
		moduleName: "Requisition To Bid",
	},
	{
		path: AppRoutings.BidSummaryGrid,
		Component: lazy(() => import("components/Bid/BidSummaryGrid")),
		pageName: MenuName.Bids,
	},
	{
		path: AppRoutings.BidDetail,
		Component: lazy(
			() => import("components/Requisition/RequisitionConversionToBid")
		),
		pageName: MenuName.Bids,
		moduleName: "Bid",
	},
	{
		path: AppRoutings.EvaluationMethodSummaryGrid,
		Component: lazy(
			() =>
				import(
					"components/MasterRecords/EvaluationMethod/EvaluationMethodSummaryGrid"
				)
		),
		pageName: MenuName.EvaluationMethods,
	},
	{
		path: AppRoutings.SystemTemplateSummaryGrid,
		Component: lazy(
			() =>
				import(
					"components/MasterRecords/SystemTemplate/SystemTemplateSummaryGrid"
				)
		),
		pageName: MenuName.SystemTemplates,
	},
	{
		path: AppRoutings.SystemTemplateDefinition,
		Component: lazy(
			() =>
				import(
					"components/MasterRecords/SystemTemplate/SystemTemplateDefinition"
				)
		),
		pageName: MenuName.SystemTemplates,
		moduleName: "System Template Definition",
	},
	{
		path: AppRoutings.TemplateTypeSummaryGrid,
		Component: lazy(
			() =>
				import("components/MasterRecords/TemplateType/TemplateTypeSummaryGrid")
		),
		pageName: MenuName.TemplateTypes,
	},
	{
		path: AppRoutings.ProcurementMethodSummaryGrid,
		Component: lazy(
			() =>
				import(
					"components/MasterRecords/ProcurementMethod/ProcurementMethodSummaryGrid"
				)
		),
		pageName: MenuName.ProcurementMethods,
	},
	{
		path: AppRoutings.ProcurementMethodDetail,
		Component: lazy(
			() =>
				import(
					"components/MasterRecords/ProcurementMethod/ProcurementMethodDetail"
				)
		),
		pageName: MenuName.ProcurementMethods,
		moduleName: "Procurement Method",
	},
	{
		path: AppRoutings.RoleMenuAccessRights,
		Component: lazy(
			() => import("components/MasterRecords/RoleMenuAccessRights")
		),
		pageName: MenuName.RoleMenuAccessRights,
		moduleName: "Role Menu Access Right",
	},
	{
		path: AppRoutings.SDBDocumentGrid,
		Component: lazy(() => import("components/MasterRecords/SBDDocument")),
		pageName: MenuName.SBDDocuments,
	},
	{
		path: AppRoutings.ContractSummaryGrid,
		Component: lazy(
			() => import("components/ContractManagement/ContractSummaryGrid")
		),
		pageName: MenuName.ContractSummaryGrid,
	},
	{
		path: AppRoutings.PORequestSummaryGrid,
		Component: lazy(
			() => import("components/ContractManagement/PORequestSummaryGrid")
		),
		pageName: MenuName.PORequestSummaryGrid,
	},
	{
		path: AppRoutings.NewContractEntry,
		Component: lazy(
			() => import("components/ContractManagement/NewContractEntry")
		),
		pageName: MenuName.ContractSummaryGrid,
		moduleName: "Contract",
	},
	{
		path: AppRoutings.NewContractEntryAction,
		Component: lazy(
			() => import("components/ContractManagement/NewContractEntry")
		),
		pageName: MenuName.ContractSummaryGrid,
		moduleName: "Contract",
	},
	{
		path: AppRoutings.BidConversionToPO,
		Component: lazy(
			() => import("components/ContractManagement/BidConversionToPO")
		),
		pageName: MenuName.PORequestSummaryGrid,
		moduleName: "Bid Conversion To PO",
	},
	{
		path: AppRoutings.BidConversionToContract,
		Component: lazy(
			() => import("components/ContractManagement/BidConversionToContract")
		),
		pageName: MenuName.ContractSummaryGrid,
		moduleName: "Bid Conversion To Contract",
	},
	{
		path: AppRoutings.BidCommitteeSummaryGrid,
		Component: lazy(
			() => import("components/Bid/BidCommittee/BidCommitteeSummaryGrid")
		),
		pageName: MenuName.BidCommittee,
	},
	{
		path: AppRoutings.BidCommitteeDetail,
		Component: lazy(
			() => import("components/Bid/BidCommittee/BidCommitteeDetail")
		),
		pageName: MenuName.BidCommittee,
		moduleName: "Bid Committee",
	},
	{
		path: AppRoutings.SystemScheduler,
		Component: lazy(() => import("components/SystemScheduler")),
		pageName: MenuName.SystemScheduler,
	},
	{
		path: AppRoutings.BidSpecificationMeeting,
		Component: lazy(() => import("components/Bid/BidSpecificationMeeting")),
		pageName: MenuName.Bids,
	},
	{
		path: AppRoutings.BidEvaluationMeeting,
		Component: lazy(() => import("components/Bid/BidEvaluationMeeting")),
		pageName: MenuName.Bids,
	},
	{
		path: AppRoutings.BidEvaluation,
		Component: lazy(() => import("components/Bid/BidEvaluationGrid")),
		pageName: MenuName.Bids,
	},
	{
		path: AppRoutings.BidEvaluationDetail,
		Component: lazy(
			() => import("components/Bid/BidEvaluationGrid/BidEvaluationDetail")
		),
		pageName: MenuName.Bids,
	},
	{
		path: AppRoutings.BidApproval,
		Component: lazy(() => import("components/Bid/BidApproval")),
		pageName: MenuName.Bids,
	},
	{
		path: AppRoutings.BidPublishing,
		Component: lazy(() => import("components/Bid/BidPublishing")),
		pageName: MenuName.Bids,
	},
	{
		path: AppRoutings.Bidding,
		Component: lazy(() => import("components/Bid/Bidding")),
		pageName: MenuName.Bids,
	},
	{
		path: AppRoutings.BidQuerySummaryGrid,
		Component: lazy(() => import("components/Bid/BidQuerySummaryGrid")),
		pageName: MenuName.BidQueries,
	},
	{
		path: AppRoutings.BidQueries,
		Component: lazy(
			() => import("components/Bid/BidQuerySummaryGrid/BidQueries")
		),
		pageName: MenuName.BidQueries,
	},
	{
		path: AppRoutings.CSDSegmentSummaryGrid,
		Component: lazy(
			() => import("components/CSDIntegration/CSDSegmentSummaryGrid")
		),
		pageName: MenuName.Segments,
	},
	{
		path: AppRoutings.CSDFamilySummaryGrid,
		Component: lazy(
			() => import("components/CSDIntegration/CSDFamilySummaryGrid")
		),
		pageName: MenuName.Families,
	},
	{
		path: AppRoutings.CSDClassSummaryGrid,
		Component: lazy(
			() => import("components/CSDIntegration/CSDClassSummaryGrid")
		),
		pageName: MenuName.Classes,
	},
	{
		path: AppRoutings.CSDCommoditySummaryGrid,
		Component: lazy(
			() => import("components/CSDIntegration/CSDCommoditySummaryGrid")
		),
		pageName: MenuName.Commodities,
	},
	{
		path: AppRoutings.SystemSetup,
		Component: lazy(() => import("components/Settings/SystemSetup")),
		pageName: MenuName.SystemSetup,
	},
	{
		path: AppRoutings.ContractManagementSettings,
		Component: lazy(
			() => import("components/Settings/ContractManagementSettings")
		),
		pageName: MenuName.ContractManagementSettings,
	},
	{
		path: AppRoutings.BudgetAllocationSummaryGrid,
		Component: lazy(
			() =>
				import(
					"components/BudgetManagement/BudgetAllocation/BudgetAllocationSummaryGrid"
				)
		),
		pageName: MenuName.BudgetAllocationSummaryGrid,
	},
	{
		path: AppRoutings.BudgetAllocationDetail,
		Component: lazy(
			() =>
				import(
					"components/BudgetManagement/BudgetAllocation/BudgetAllocationWrapper"
				)
		),
		pageName: MenuName.BudgetAllocationSummaryGrid,
		moduleName: "budget allocation",
	},
	{
		path: AppRoutings.GlAccountSummaryGrid,
		Component: lazy(
			() =>
				import(
					"components/MasterRecords/GlAccountSummaryGrid/GlAccountSummaryGridWrapper"
				)
		),
		pageName: MenuName.GlAccounts,
	},
	{
		path: AppRoutings.GlAccountDetail,
		Component: lazy(
			() =>
				import("components/MasterRecords/GlAccountSummaryGrid/GlAccountDetail")
		),
		pageName: MenuName.GlAccounts,
		moduleName: "Gl Account",
	},
	{
		path: AppRoutings.ErpDbCompanySummaryGrid,
		Component: lazy(
			() =>
				import("components/MasterRecords/ErpDbCompany/ErpDbCompanySummaryGrid")
		),
		pageName: MenuName.ErpDbCompanies,
	},
	{
		path: AppRoutings.ErpDbCompanyDetail,
		Component: lazy(
			() => import("components/MasterRecords/ErpDbCompany/ErpDbCompanyDetail")
		),
		pageName: MenuName.ErpDbCompanies,
		moduleName: "erp db company",
	},
	{
		path: AppRoutings.BudgetChangeOrderSummaryGrid,
		Component: lazy(
			() =>
				import("components/BudgetManagement/ChangeOrder/ChangeOrderSummaryGrid")
		),
		pageName: MenuName.BudgetChangeOrders,
	},
	{
		path: AppRoutings.BudgetChangeOrderDetail,
		Component: lazy(
			() => import("components/BudgetManagement/ChangeOrder/ChangeOrderDetail")
		),
		pageName: MenuName.BudgetChangeOrders,
		moduleName: "budget change orders",
	},
	{
		path: AppRoutings.UserProfile,
		Component: lazy(() => import("components/UserManagement/UserProfile")),
		pageName: "",
	},
	{
		path: AppRoutings.BudgetAllocationDetailAction,
		Component: lazy(
			() =>
				import(
					"components/BudgetManagement/BudgetAllocation/BudgetAllocationWrapper"
				)
		),
		pageName: MenuName.BudgetAllocationSummaryGrid,
		moduleName: "Budget Allocation",
	},
	{
		path: AppRoutings.AdSync,
		Component: lazy(() => import("components/Settings/AdSync")),
		pageName: MenuName.AdSync,
	},
	{
		path: AppRoutings.BudgetingProcessDashboard,
		Component: lazy(() => import("components/Dashboard/BudgetingProcess")),
		pageName: MenuName.BudgetingProcessDashboard,
		moduleName: "Budgeting Process Dashboard",
	},
	{
		path: AppRoutings.FinancialPerformanceDashboard,
		Component: lazy(() => import("components/Dashboard/FinancialPerformance")),
		pageName: MenuName.FinancialPerformanceDashboard,
		moduleName: "Financial Performance Dashboard",
	},
	{
		path: AppRoutings.FixedAssetsTrackingDashboard,
		Component: lazy(() => import("components/Dashboard/FixedAssetsTracking")),
		pageName: MenuName.FixedAssetsTrackingDashboard,
		moduleName: "Fixed Assets Tracking Dashboard",
	},
	{
		path: AppRoutings.SupplyChainManagementPortal,
		Component: lazy(() => import("components/SupplyChainManagementPortal")),
		pageName: MenuName.SupplyChainManagementPortal,
		moduleName: "Supply Chain Management Portal",
	},
	{
		path: AppRoutings.CompanyProfile,
		Component: lazy(() => import("components/CompanyProfile")),
		pageName: MenuName.CompanyProfile,
		moduleName: "Company Profile",
	},
	{
		path: AppRoutings.ExternalUsers,
		Component: lazy(() => import("components/UserManagement/ExternalUser")),
		pageName: MenuName.ExternalUsers,
	},
	{
		path: AppRoutings.ExpenditureSummaryGrid,
		Component: lazy(() => import("components/ExpenditureSummaryGrid")),
		pageName: MenuName.ExpenditureSummaryGrid,
	},
	{
		path: AppRoutings.ExpenditureDetail,
		Component: lazy(
			() => import("components/ExpenditureSummaryGrid/ExpenditureDetailContent")
		),
		pageName: MenuName.ExpenditureSummaryGrid,
	},
	{
		path: AppRoutings.ContractManagementDashboard,
		Component: lazy(() => import("components/Dashboard/ContractManagement")),
		pageName: MenuName.ContractManagementDashboard,
		moduleName: "Contract Management Dashboard",
	},
	{
		path: AppRoutings.PerformanceEvaluationSummaryGrid,
		Component: lazy(
			() => import("components/ContractManagement/PerformanceEvaluation")
		),
		pageName: MenuName.PerformanceEvaluationSummaryGrid,
	},
	{
		path: AppRoutings.PerformanceEvaluationDetail,
		Component: lazy(
			() =>
				import(
					"components/ContractManagement/PerformanceEvaluation/PerformanceEvaluationDetail"
				)
		),
		pageName: MenuName.PerformanceEvaluationDetail,
	},
	{
		path: AppRoutings.PerformanceEvaluationHistoryDetail,
		Component: lazy(
			() =>
				import(
					"components/ContractManagement/PerformanceEvaluation/PerformanceEvaluationHistory"
				)
		),
		pageName: MenuName.PerformanceEvaluationHistoryDetail,
	},
	{
		path: AppRoutings.InvoiceUploadSummaryGrid,
		Component: lazy(
			() =>
				import(
					"components/SupplyChainManagementPortal/InvoiceUpload/InvoiceUploadSummaryGrid"
				)
		),
		pageName: MenuName.InvoiceUploadSummaryGrid,
		moduleName: "Invoice Upload Summary Grid",
	},
	{
		path: AppRoutings.SupplierDocumentUpload,
		Component: lazy(
			() =>
				import(
					"components/SupplyChainManagementPortal/SupplierDocumentUpload/SupplierDocumentUploadWrapper"
				)
		),
		pageName: MenuName.InvoiceUploadSummaryGrid,
		moduleName: "Supplier Document Upload",
	},
	{
		path: AppRoutings.ValidityPeriodSummaryGrid,
		Component: lazy(
			() =>
				import(
					"components/MasterRecords/ValidityPeriod/ValidityPeriodSummaryGrid"
				)
		),
		pageName: MenuName.ValidityPeriods,
	},
	{
		path: AppRoutings.RegionSummaryGrid,
		Component: lazy(
			() =>
				import(
					"components/Settings/OrganisationalUnits/Region/RegionSummaryGrid"
				)
		),
		pageName: MenuName.Regions,
	},
	{
		path: AppRoutings.SupplierBidSummary,
		Component: lazy(
			() =>
				import("components/SupplyChainManagementPortal/SupplierBidSummaryGrid")
		),
		pageName: MenuName.SupplierBidSummary,
		moduleName: "Supplier Bid Summary",
	},
	{
		path: AppRoutings.QuerySummary,
		Component: lazy(
			() => import("components/SupplyChainManagementPortal/QuerySummaryGrid")
		),
		pageName: MenuName.QuerySummary,
		moduleName: "Supplier Bid Summary",
	},
	{
		path: AppRoutings.SupplierBidDetail,
		Component: lazy(
			() => import("components/SupplyChainManagementPortal/ManageBid")
		),
		pageName: MenuName.SupplierBidSummary,
		moduleName: "Supplier Bid Detail",
	},
	{
		path: AppRoutings.BidEntryAction,
		Component: lazy(
			() => import("components/Requisition/RequisitionConversionToBid")
		),
		pageName: MenuName.Bids,
		moduleName: "Bid",
	},
	{
		path: AppRoutings.BidView,
		Component: lazy(
			() => import("components/Requisition/RequisitionConversionToBid")
		),
		pageName: MenuName.Bids,
		moduleName: "Bid",
	},
	{
		path: AppRoutings.BudgetChangeOrderEntryAction,
		Component: lazy(
			() => import("components/BudgetManagement/ChangeOrder/ChangeOrderDetail")
		),
		pageName: MenuName.BudgetChangeOrders,
		moduleName: "Budget change orders",
	},
	{
		path: AppRoutings.AuditTrails,
		Component: lazy(
			() => import("components/AuditTrails/UserActivitySummaryGrid")
		),
		pageName: MenuName.AuditTrails,
	},
	{
		path: AppRoutings.UserAccessAudit,
		Component: lazy(
			() => import("components/AuditTrails/UserAccessDetailAudit")
		),
		pageName: MenuName.AuditTrails,
	},
	{
		path: AppRoutings.DetailUserActivity,
		Component: lazy(
			() => import("components/AuditTrails/DetailedUserActivity")
		),
		pageName: MenuName.AuditTrails,
	},
	{
		path: AppRoutings.TransactionAudit,
		Component: lazy(() => import("components/AuditTrails/TransactionAudit")),
		pageName: MenuName.AuditTrails,
	},
	{
		path: AppRoutings.VariationSummaryGrid,
		Component: lazy(
			() => import("components/ContractManagement/VariationSummaryGrid")
		),
		pageName: MenuName.VariationSummaryGrid,
	},
	{
		path: AppRoutings.DivisionSummaryGrid,
		Component: lazy(
			() =>
				import(
					"components/Settings/OrganisationalUnits/Division/DivisionSummaryGrid"
				)
		),
		pageName: MenuName.Divisions,
	},
	{
		path: AppRoutings.ContractPOEntry,
		Component: lazy(
			() => import("components/ContractManagement/ContractPOEntry")
		),
		pageName: MenuName.PORequestSummaryGrid,
		moduleName: "Contract PO",
	},
	{
		path: AppRoutings.ContractPOEntryAction,
		Component: lazy(
			() => import("components/ContractManagement/ContractPOEntry")
		),
		pageName: MenuName.PORequestSummaryGrid,
		moduleName: "Contract PO",
	},
	{
		path: AppRoutings.ContractVariationEntry,
		Component: lazy(
			() => import("components/ContractManagement/ContractVariationEntry")
		),
		pageName: MenuName.VariationSummaryGrid,
		moduleName: "Contract Variation",
	},
	{
		path: AppRoutings.ContractVariationEntryAction,
		Component: lazy(
			() => import("components/ContractManagement/ContractVariationEntry")
		),
		pageName: MenuName.VariationSummaryGrid,
		moduleName: "Contract Variation",
	},
	{
		path: AppRoutings.EvaluationCategorySummaryGrid,
		Component: lazy(
			() =>
				import(
					"components/MasterRecords/EvaluationCategory/EvaluationCategorySummeryGrid"
				)
		),
		pageName: MenuName.EvaluationCategories,
	},
	{
		path: AppRoutings.PurchaseOrderSummaryGrid,
		Component: lazy(
			() => import("components/ContractManagement/PurchaseOrderSummaryGrid")
		),
		pageName: MenuName.PurchaseOrderSummaryGrid,
	},
	{
		path: AppRoutings.EvaluationElementSummaryGrid,
		Component: lazy(
			() =>
				import(
					"components/MasterRecords/EvaluationElement/EvaluationElementSummaryGrid"
				)
		),
		pageName: MenuName.EvaluationElements,
	},
	{
		path: AppRoutings.FinanceStatementDashboard,
		Component: lazy(() => import("components/Dashboard/FinanceStatement")),
		pageName: MenuName.FinanceStatementDashboard,
		moduleName: "Finance Statement Dashboard",
	},
	{
		path: AppRoutings.CSDSupplierMaintenance,
		Component: lazy(
			() => import("components/CSDIntegration/CSDSupplierMaintenanceGrid")
		),
		pageName: MenuName.CSDSupplierMaintenance,
	},
	{
		path: AppRoutings.CSDDailyUpdates,
		Component: lazy(
			() => import("components/CSDIntegration/CSDDailyUpdatesGrid")
		),
		pageName: MenuName.CSDDailyUpdates,
	},
	{
		path: AppRoutings.CSDRFQs,
		Component: lazy(
			() => import("components/CSDIntegration/CSDRFQSummaryGrid")
		),
		pageName: MenuName.CSDRFQs,
	},
	{
		path: AppRoutings.CSDDailyUpdateDetailedGrid,
		Component: lazy(
			() => import("components/CSDIntegration/CSDDailyUpdateDetailedGrid")
		),
		pageName: MenuName.CSDDailyDetailedUpdate,
	},
	{
		path: AppRoutings.ContractCoordinators,
		Component: lazy(
			() =>
				import("components/ContractManagement/ContractCoordinatorSummaryGrid")
		),
		pageName: MenuName.ContractCoordinator,
	},
	{
		path: AppRoutings.ConsolidatedProcurementPlanning,
		Component: lazy(
			() => import("components/ProcurementPlanning/APP/APPSummaryGrid")
		),
		pageName: MenuName.ConsolidatedProcurementPlan,
	},
	{
		path: AppRoutings.AppDetail,
		Component: lazy(
			() => import("components/ProcurementPlanning/APP/APPDetail")
		),
		pageName: MenuName.ConsolidatedProcurementPlan,
		moduleName: "Procurement Planning",
	},
	{
		path: AppRoutings.DepartmentalProcurementPlanning,
		Component: lazy(
			() =>
				import(
					"components/ProcurementPlanning/Departmental/DepartmentalSummaryGrid"
				)
		),
		pageName: MenuName.DepartmentalProcurementPlan,
	},
	{
		path: AppRoutings.DepartmentalProcurementPlanEntry,
		Component: lazy(
			() =>
				import("components/ProcurementPlanning/Departmental/DepartmentalDetail")
		),
		pageName: MenuName.DepartmentalProcurementPlan,
		moduleName: "Procurement Planning",
	},
	{
		path: AppRoutings.PriceAndBeeSetting,
		Component: lazy(
			() =>
				import(
					"components/ModuleSettings/PriceAndBeeSettings/PriceAndBeeSettingSummaryGrid"
				)
		),
		pageName: MenuName.PriceAndBeeSetting,
	},
	{
		path: AppRoutings.PriceAndBeeSettingEntry,
		Component: lazy(
			() =>
				import(
					"components/ModuleSettings/PriceAndBeeSettings/PriceAndBeeSettingDetail"
				)
		),
		pageName: MenuName.PriceAndBeeSetting,
		moduleName: "Sourcing",
	},
	{
		path: AppRoutings.VendorBidQuery,
		Component: lazy(
			() => import("components/SupplyChainManagementPortal/VendorBidQuery")
		),
		pageName: MenuName.QuerySummary,
	},
	{
		path: AppRoutings.PerformanceEvaluationSettingSummaryGrid,
		Component: lazy(
			() =>
				import(
					"components/MasterRecords/PerformanceEvaluation/PerformanceEvaluationSummaryGrid"
				)
		),
		pageName: MenuName.PerformanceEvaluationSettings,
	},
	{
		path: AppRoutings.PerformanceEvaluationSettingDetail,
		Component: lazy(
			() =>
				import(
					"components/MasterRecords/PerformanceEvaluation/PerformanceEvaluationDetail"
				)
		),
		pageName: MenuName.PerformanceEvaluationSettings,
	},
	{
		path: AppRoutings.SupplierBillingPeriodDetail,
		Component: lazy(
			() =>
				import(
					"components/SupplyChainManagementPortal/InvoiceUpload/SupplierPeriodBillingEntry"
				)
		),
		pageName: MenuName.InvoiceUploadSummaryGrid,
	},
	{
		path: AppRoutings.ChangeOrderRequestEntry,
		Component: lazy(
			() =>
				import(
					"components/BudgetManagement/ChangeOrderRequest/ChangeOrderRequestEntry"
				)
		),
		pageName: MenuName.ChangeOrderRequestSummaryGrid,
		moduleName: "Budget Management",
	},
	{
		path: AppRoutings.ChangeOrderRequestEntryAction,
		Component: lazy(
			() =>
				import(
					"components/BudgetManagement/ChangeOrderRequest/ChangeOrderRequestEntry"
				)
		),
		pageName: MenuName.ChangeOrderRequestSummaryGrid,
		moduleName: "Budget Management",
	},
	{
		path: AppRoutings.ChangeOrderRequestSummaryGrid,
		Component: lazy(
			() =>
				import(
					"components/BudgetManagement/ChangeOrderRequest/ChangeOrderRequestSummaryGrid"
				)
		),
		pageName: MenuName.ChangeOrderRequestSummaryGrid,
		moduleName: "Budget Management",
	},
	{
		path: AppRoutings.OrganisationalStructureEntry,
		Component: lazy(
			() =>
				import(
					"components/Settings/OrganisationalUnits/OrganisationalStructureEntry"
				)
		),
		pageName: MenuName.OrganisationalUnits,
	},
	{
		path: AppRoutings.ProjectDetail,
		Component: lazy(
			() =>
				import("components/Settings/OrganisationalUnits/Project/ProjectDetail")
		),
		pageName: MenuName.ProjectDetail,
	},
	{
		path: AppRoutings.ProjectSummaryGrid,
		Component: lazy(
			() =>
				import(
					"components/Settings/OrganisationalUnits/Project/ProjectSummaryGrid"
				)
		),
		pageName: MenuName.ProjectSummaryGrid,
	},
	{
		path: AppRoutings.ProgrammeDetails,
		Component: lazy(
			() =>
				import(
					"components/Settings/OrganisationalUnits/Programme/ProgrammeDetail"
				)
		),
		pageName: MenuName.ProgrammeDetails,
	},
	{
		path: AppRoutings.DivisionDetail,
		Component: lazy(
			() =>
				import(
					"components/Settings/OrganisationalUnits/Division/DivisionDetail"
				)
		),
		pageName: MenuName.Divisions,
		moduleName: "Division",
	},
	{
		path: AppRoutings.SubDivisionDetail,
		Component: lazy(
			() =>
				import(
					"components/Settings/OrganisationalUnits/SubDivision/SubDivisionDetail"
				)
		),
		pageName: MenuName.SubDivisions,
		moduleName: "SubDivision",
	},
	{
		path: AppRoutings.SubDivisionSummaryGrid,
		Component: lazy(
			() =>
				import(
					"components/Settings/OrganisationalUnits/SubDivision/SubDivisionSummaryGrid"
				)
		),
		pageName: MenuName.Divisions,
	},
	{
		path: AppRoutings.SubRegionDetail,
		Component: lazy(
			() =>
				import(
					"components/Settings/OrganisationalUnits/SubRegion/SubRegionDetail"
				)
		),
		pageName: MenuName.SubRegions,
		moduleName: "SubRegion",
	},
	{
		path: AppRoutings.RegionDetail,
		Component: lazy(
			() =>
				import("components/Settings/OrganisationalUnits/Region/RegionDetail")
		),
		pageName: MenuName.Regions,
		moduleName: "Region",
	},
	{
		path: AppRoutings.SubRegionSummaryGrid,
		Component: lazy(
			() =>
				import(
					"components/Settings/OrganisationalUnits/SubRegion/SubRegionSummaryGrid"
				)
		),
		pageName: MenuName.SubRegions,
	},
	{
		path: AppRoutings.SubDepartmentDetail,
		Component: lazy(
			() =>
				import(
					"components/Settings/OrganisationalUnits/SubDepartment/SubDepartmentDetail"
				)
		),
		pageName: MenuName.SubDepartments,
		moduleName: "SubDepartment",
	},
	{
		path: AppRoutings.SubDepartmentSummaryGrid,
		Component: lazy(
			() =>
				import(
					"components/Settings/OrganisationalUnits/SubDepartment/SubDepartmentSummaryGrid"
				)
		),
		pageName: MenuName.SubDepartments,
	},
	// {
	// 	path: AppRoutings.BudgetSummaryGrid,
	// 	Component: lazy(
	// 		() => import("components/BudgetManagement/BudgetSummaryGrid")
	// 	),
	// 	pageName: MenuName.BudgetSummaryGridList,
	// 	moduleName: "Budget Management",
	// },
	{
		path: AppRoutings.RequisitionSettings,
		Component: lazy(
			() =>
				import(
					"components/Settings/SystemSetup/ModuleSettingsTabContent/RequisitionSettings"
				)
		),
		pageName: MenuName.RequisitionSettings,
		moduleName: "Module Settings",
	},
	{
		path: AppRoutings.SourcingSettings,
		Component: lazy(
			() =>
				import(
					"components/Settings/SystemSetup/ModuleSettingsTabContent/SourcingSettings"
				)
		),
		pageName: MenuName.SourcingSettings,
		moduleName: "Module Settings",
	},
	{
		path: AppRoutings.DepartmentInstanceDetail,
		Component: lazy(
			() =>
				import(
					"components/Settings/OrganisationalUnits/Department/DepartmentInstanceDetail"
				)
		),
		pageName: MenuName.Departments,
		moduleName: "Department",
	},
];
